import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Box,
  Avatar,
  Pagination,
} from "@mui/material";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import API_URL from "../../config";
import { formatDistanceToNow } from "date-fns";
import API_BASE_URL from "../../utils/api";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
const ThreadList = ({ reload }) => {
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user } = useAuth();
  const handleMenuOpen = (event, thread) => {
    setAnchorEl(event.currentTarget);
    setSelectedThread(thread);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedThread(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/threads/${selectedThread._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      alert("Thread deleted");
      setThreads((prev) => prev.filter((t) => t._id !== selectedThread._id));
    } catch (err) {
      alert("Failed to delete thread");
      console.error(err);
    } finally {
      handleCloseDialog();
    }
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/threads?page=${page}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setThreads(res.data.threads);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error("Failed to fetch threads:", err);
      }
    };

    fetchThreads();
  }, [page, reload]);
  return (
    <Box sx={{ mt: 4 }}>
      {threads
        .filter((thread) => {
          // Show all threads if they are visible to everyone
          // Show workshop-only threads only if user has access
          return thread.visibility === "all" || user?.workshopAccess;
        })
        .map((thread) => {
          const postedBy = thread.isGuestThread
            ? thread.guestName || "Guest User"
            : thread.author?.name || "Unknown User";

          const profileImage = thread.isGuestThread
            ? ""
            : thread.author?.profileImage;

          const canManageThread =
            (!thread.isGuestThread && user?._id === thread.author?._id) ||
            user?.role === "admin";

          return (
            <Box
              key={thread._id}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 3,
                border: "1px solid #eee",
                backgroundColor: "#fff",
                position: "relative",
                transition: "0.2s",
                "&:hover": {
                  boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {canManageThread && (
                <IconButton
                  onClick={(e) => handleMenuOpen(e, thread)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <MoreVertIcon />
                </IconButton>
              )}

              <Box display="flex" alignItems="center" mb={1}>
                <Avatar src={profileImage} alt={postedBy} sx={{ mr: 1 }}>
                  {postedBy?.charAt(0)}
                </Avatar>

                <Typography variant="subtitle2">{postedBy}</Typography>

                {thread.isGuestThread && (
                  <Box
                    sx={{
                      ml: 1,
                      backgroundColor: "#fff3cd",
                      color: "#856404",
                      px: 1,
                      py: 0.2,
                      borderRadius: "999px",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    Guest
                  </Box>
                )}

                <Typography variant="caption" sx={{ ml: 1, color: "gray" }}>
                  {formatDistanceToNow(
                    new Date(thread.activityDate || thread.createdAt),
                    {
                      addSuffix: true,
                    }
                  )}
                </Typography>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={0.5}
                flexWrap="wrap"
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, lineHeight: 1.2, cursor: "pointer" }}
                  onClick={() => navigate(`/thread/${thread._id}`)}
                >
                  {thread.title}
                </Typography>

                {thread.visibility === "workshop" && (
                  <Box
                    sx={{
                      backgroundColor: "#FFF4CC",
                      color: "#6A4D00",
                      px: 1.2,
                      py: 0.3,
                      borderRadius: "999px",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      border: "1px solid #FFE08A",
                    }}
                  >
                    Workshop Only
                  </Box>
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.6,
                }}
              >
                {stripHtml(thread.content)}
              </Typography>

              <Button
                size="small"
                onClick={() => navigate(`/thread/${thread._id}`)}
                sx={{
                  mt: 1.2,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                View thread →
              </Button>
            </Box>
          );
        })}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => navigate(`/thread/edit/${selectedThread?._id}`)}
        >
          Edit
        </MenuItem>

        <MenuItem onClick={handleOpenDialog}>Delete</MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Are you sure you want to delete this thread?</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            <strong>Title:</strong> {selectedThread?.title}
          </Typography>
          <Typography variant="subtitle2">
            <strong>Author:</strong> {selectedThread?.author?.name}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Pagination
        count={totalPages}
        page={page}
        onChange={(e, value) => setPage(value)}
        sx={{ mt: 3 }}
      />
    </Box>
  );
};
export default ThreadList;
